/* cf-price-parity.js - Click Fencing, 2026-08-22
 *
 * Makes every quote line carry the price Shopify will actually charge, so the calculator
 * checkout can post REAL line items instead of collapsing to the one-cent placeholder.
 *
 * THE BUG THIS FIXES
 * ------------------
 * Calculator checkout was arriving as a single line - variant 42909527670878 "Quote Total"
 * at $0.01, quantity = the grand total in cents. A $1,068.06 quote checked out as
 * "Quote Total x 106806". Correct money, but no SKUs on the order, no picking list and no
 * inventory movement.
 *
 * cf-real-line-items.js was doing this deliberately. Its own console said so:
 *
 *     [cf-real-line-items] falling back to quote-total line: real lines exceed quote by 6128c
 *
 * It builds the real lines, reads the cart back, and refuses to proceed if the cart totals
 * MORE than the quote - the rule being that a customer must never be charged more than they
 * were quoted. That guard is right and is left exactly as it is.
 *
 * It was firing because four hardware lines were still priced EX-GST in the quote while the
 * store had moved to GST-inclusive display, so Shopify charged 10% more than the quote said:
 *
 *     Gate Panel 800 x 1200mm      64.30 -> 70.73
 *     Core Drill Spigot 240mm      55.15 -> 60.67   (x6 = $33.12 of the gap)
 *     Spring Hinge Glass to Glass 153.62 -> 168.98
 *     Gate Latch Glass to Glass    63.65 -> 70.02
 *                                          $61.28 short on a $1,068.06 quote
 *
 * WHY THOSE FOUR AND NOT THE GLASS PANELS
 * ---------------------------------------
 * cf-quote-truth.js already adopts the Shopify price, but only where it has one to adopt.
 * Its resolve() returns `cents: null` for a line that arrives with a variantId already set:
 *
 *     if (b.variantId) return { id: Number(b.variantId), cents: null };
 *
 * Glass panels carry a SKU ("48196") and go down the width/SKU path, which brings a live
 * price with it, so they were already correct. These four carry **no SKU at all** - only a
 * variantId - so they hit that branch, kept the calculator's stale figure, and nothing
 * downstream ever noticed.
 *
 * WHAT THIS DOES
 * --------------
 * Reads the live storefront price for every line that resolves to a variant and writes it
 * onto the quote, then recomputes the line and grand totals. After this the quote and the
 * cart agree to the cent, cf-real-line-items reconciles at diff === 0, and the order carries
 * real products.
 *
 * Prices come from /products.json - the same public feed the calculators already use via
 * cf-sku-prices.js, and the same feed Shopify charges from. No admin auth.
 *
 * Deliberately NOT touched: delivery, pickup, inspection and tax lines. Shopify owns
 * shipping and tax, and cf-real-line-items already subtracts both from its target.
 *
 * Idempotent - writing the live price over the live price is a no-op, so it is safe on every
 * re-render. Must load AFTER cf-quote-truth.js, and waits for it before running.
 */
(function () {
  'use strict';
  if (window.__cfPriceParityInstalled) return;
  window.__cfPriceParityInstalled = true;

  if (!/quote-review/.test(location.pathname)) return;

  var CALCS = ['ullc', 'blade', 'flattop', 'perfmesh'];
  var priceByVariant = null;

  function isSkippable(b) {
    var s = String(b.category || '') + ' ' + String(b.item || '');
    return /delivery|pickup|inspection fee/i.test(s)
        || /^tax$/i.test(String(b.category || '').trim())
        || /\bgst\b/i.test(String(b.item || ''));
  }

  /* Same precedence cf-real-line-items uses to pick a variant, so the two agree on which
     product a line is. If they disagreed the totals could never tie. */
  function variantOf(b) {
    if (b.variantId) return Number(b.variantId) || null;
    var sku = b.sku == null ? '' : String(b.sku);
    if (/^\d{12,}$/.test(sku)) return Number(sku) || null;
    if (sku && typeof window.CF_VARIANT === 'function') {
      try { var v = window.CF_VARIANT(sku); if (v) return Number(v) || null; } catch (e) {}
    }
    return null;
  }

  function loadPrices() {
    if (priceByVariant) return Promise.resolve(priceByVariant);
    var acc = {}, page = 1;
    function next() {
      return fetch('/products.json?limit=250&page=' + page)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (!d || !d.products || !d.products.length || page > 8) return;
          d.products.forEach(function (p) {
            (p.variants || []).forEach(function (v) {
              acc[String(v.id)] = Math.round(parseFloat(v.price) * 100);
            });
          });
          page++;
          return next();
        });
    }
    return next().then(function () { priceByVariant = acc; return acc; })
                 .catch(function () { priceByVariant = {}; return priceByVariant; });
  }

  function locate() {
    var pill = document.querySelector('.qr-quote-pill.is-active');
    var label = pill ? String(pill.textContent || '').trim() : '';
    if (!label) return null;
    for (var i = 0; i < CALCS.length; i++) {
      var c = CALCS[i], active = null;
      try { active = JSON.parse(localStorage.getItem(c + '_active_quote') || 'null'); } catch (e) {}
      if (active && active.breakdown && active.breakdown.length
          && String(active.quoteName || '').trim() === label) {
        return { key: c + '_active_quote', idx: -1, quote: active };
      }
      var arr = [];
      try { arr = JSON.parse(localStorage.getItem(c + '_quotes') || '[]') || []; } catch (e) {}
      if (Object.prototype.toString.call(arr) !== '[object Array]') continue;
      for (var j = 0; j < arr.length; j++) {
        var q = arr[j];
        if (q && q.breakdown && q.breakdown.length
            && String(q.quoteName || '').trim() === label) {
          return { key: c + '_quotes', idx: j, quote: q };
        }
      }
    }
    return null;
  }

  function save(ref) {
    if (ref.idx < 0) { localStorage.setItem(ref.key, JSON.stringify(ref.quote)); return; }
    var arr = JSON.parse(localStorage.getItem(ref.key) || '[]');
    arr[ref.idx] = ref.quote;
    localStorage.setItem(ref.key, JSON.stringify(arr));
  }

  function correct(q, prices) {
    var changes = [], total = 0;
    q.breakdown.forEach(function (b) {
      if (!isSkippable(b)) {
        var vid = variantOf(b);
        var live = vid ? prices[String(vid)] : null;
        if (live != null) {
          var was = Math.round((Number(b.unitPrice) || 0) * 100);
          if (was !== live) {
            b.unitPrice = live / 100;
            changes.push(String(b.item || '').slice(0, 40)
              + ' $' + (was / 100).toFixed(2) + ' -> $' + (live / 100).toFixed(2));
          }
        }
      }
      b.totalPrice = Math.round((Number(b.quantity) || 0) * (Number(b.unitPrice) || 0) * 100) / 100;
      total += b.totalPrice;
    });
    var newTotal = Math.round(total * 100) / 100;
    var moved = newTotal !== q.grandTotal;
    q.grandTotal = newTotal;
    /* The editor's baseline is keyed on breakdown index and holds prices; drop it so it
       recaptures from the corrected figures rather than re-applying the old ones. */
    if (changes.length) { delete q.cfBaseline; delete q.cfAdjustments; }
    return { changes: changes, moved: moved, total: newTotal };
  }

  var done = {}, tries = 0;

  function run() {
    if (tries++ > 40) return;
    try {
      var ref = locate();
      if (!ref) { setTimeout(run, 500); return; }
      var name = String(ref.quote.quoteName || (ref.key + ':' + ref.idx));
      if (done[name]) return;

      loadPrices().then(function (prices) {
        try {
          if (done[name]) return;
          done[name] = true;
          var res = correct(ref.quote, prices);
          if (!res.changes.length) {
            console.log('[cf-price-parity] ' + name + ' already matches Shopify');
            return;
          }
          save(ref);
          console.log('[cf-price-parity] ' + name + ': ' + res.changes.length
            + ' line(s) repriced to Shopify, total now $' + res.total.toFixed(2)
            + ' - ' + res.changes.join('; '));
          var pill = document.querySelector('.qr-quote-pill.is-active');
          if (pill) pill.click();
        } catch (e) {
          console.warn('[cf-price-parity] correction failed', e);
        }
      });
    } catch (e) {
      console.warn('[cf-price-parity] run failed', e);
      setTimeout(run, 800);
    }
  }

  /* cf-quote-truth rewrites the same quote. Let it finish first, or its pass would put the
     stale figures back over the top of these. */
  function start() {
    var waited = 0;
    (function waitForTruth() {
      if (window.__cfQuoteTruthInstalled || waited > 6000) { setTimeout(run, 900); return; }
      waited += 300;
      setTimeout(waitForTruth, 300);
    })();
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.qr-quote-pill')) {
        setTimeout(run, 700);
      }
    }, true);
    console.log('[cf-price-parity] installed');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
